import { ReactElement } from "react";
import { AperitifItem, MenuFormat } from "features/editor/MenuDocumentModel";
import { EditorItem, getMmCssValue, getWineMarginBottomMm } from "features/editor/editorUtils";

interface AperitifPreviewItemProps {
    aperitifItem: EditorItem<AperitifItem>
    format: MenuFormat
    isPreview?: boolean
}

export function getAperitifHeightMm(format: MenuFormat, item: AperitifItem): number {
    const hasImage = !!item.imageUrl;
    switch (format) {
        case "a4":     return hasImage ? 28 : 16;
        case "a5":     return hasImage ? 25 : 13;
        case "a4-half": return hasImage ? 27 : 14;
        default:        return hasImage ? 28 : 16;
    }
}

export function AperitifPreviewItem({ aperitifItem, format }: AperitifPreviewItemProps): ReactElement {
    const item = aperitifItem.menuItem;
    const hasImage = !!item.imageUrl;
    const isA4 = format === 'a4';
    const isA5 = format === 'a5';
    const height = getAperitifHeightMm(format, item);

    return (
        <article
            className="w-full overflow-hidden flex items-stretch gap-3 relative"
            style={{
                height: getMmCssValue(height),
                marginBottom: getMmCssValue(getWineMarginBottomMm(format), "0")
            }}
        >
            {hasImage && (
                <div className="self-stretch w-[14mm] flex-none overflow-hidden flex justify-center items-center">
                    <img
                        alt={item.name}
                        className="object-cover object-center"
                        src={item.imageUrl!}
                        style={{ height: getMmCssValue(height) }}
                    />
                </div>
            )}

            <div
                className="flex-auto flex flex-col justify-center py-[.5mm]"
                style={{ gap: isA4 ? '0.3rem' : '0.2rem' }}
            >
                <div className="flex items-baseline">
                    <h2 className={`font-barlow font-medium leading-none
                        ${isA4 ? 'text-[14pt]' : isA5 ? 'text-[12pt]' : 'text-[10pt]'}`}>
                        {item.name}
                    </h2>

                    {item.size && <>
                        <span className={`font-barlow text-text leading-none mx-1
                            ${isA4 ? 'text-[10pt]' : 'text-[8pt]'}`}>
                            |
                        </span>
                        <span className={`font-barlow text-text leading-none whitespace-nowrap
                            ${isA4 ? 'text-[10pt]' : 'text-[8pt]'}`}>
                            {item.size}
                        </span>
                    </>}

                    <div className="flex-auto px-2">
                        <hr className="h-px border-dotted border-t border-text" />
                    </div>

                    <span className={`font-barlow font-medium leading-none flex-none whitespace-nowrap
                        ${isA4 ? 'text-[10pt]' : 'text-[8pt]'}`}>
                        {item.price}
                    </span>
                </div>

                {item.description && (
                    <div
                        className={`font-barlow leading-tight overflow-hidden
                            ${isA4 ? 'text-[8pt]' : 'text-[7pt]'}`}
                        style={{ maxHeight: isA4 ? '29pt' : '27pt' }}
                    >
                        {item.description}
                    </div>
                )}
            </div>
        </article>
    );
}
